import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { showActivity, hideActivity } from '../activity';
import { API } from '../config';



function Spoofer() {
  const [fileType, setFileType] = useState('image');
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [intensity, setIntensity] = useState('medium');
  const [count, setCount] = useState(100);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [downloadUrl, setDownloadUrl] = useState(null);
  const pollRef = useRef(null);
  const jobIdRef = useRef(null);
  const fileInputRef = useRef(null);
  const mountedRef = useRef(true);

  // Resume polling if a job was running when we navigated away
  useEffect(() => {
    mountedRef.current = true;
    const savedJobId = localStorage.getItem('spoofJobId');
    if (savedJobId) {
      jobIdRef.current = savedJobId;
      setGenerating(true);
      setStatus('Reconnecting...');
      showActivity('Reconnecting to spoof job...');

      pollRef.current = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/api/spoofer/status/${savedJobId}?t=${Date.now()}`);
          const job = statusRes.data;
          if (job.status === 'running') {
            setProgress({ done: job.done, total: job.total });
            setStatus(`Generating ${job.done} of ${job.total}...`);
            showActivity(`Spoofing ${job.done}/${job.total}...`);
          } else if (job.status === 'zipping') {
            setProgress({ done: job.total, total: job.total });
            setStatus('Creating zip archive...');
          } else if (job.status === 'done') {
            stopPolling();
            setProgress({ done: job.total, total: job.total });
            setGenerating(false);
            setStatus('✓ Ready to download');
            setDownloadUrl(job.url);
            hideActivity();
            localStorage.removeItem('spoofJobId');
          } else {
            stopPolling();
            setGenerating(false);
            setStatus(job.status === 'cancelled' ? 'Cancelled.' : '');
            if (job.status === 'error') setError(job.error || 'Unknown error');
            hideActivity();
            localStorage.removeItem('spoofJobId');
          }
        } catch (err) {
          stopPolling();
          setGenerating(false);
          hideActivity();
          localStorage.removeItem('spoofJobId');
        }
      }, 500);
    }
    return () => {
      // Leaving the page: stop polling and clear the progress toast. The job
      // keeps running server-side (its id stays saved → resumed on return).
      mountedRef.current = false;
      stopPolling();
      hideActivity();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleImageSelect = (files) => {
    if (!files || files.length === 0) return;
    const validFiles = Array.from(files).filter(f =>
      fileType === 'gif' ? f.type === 'image/gif' : (f.type === 'image/jpeg' || f.type === 'image/png')
    );
    setImages(validFiles);
    setPreviews(validFiles.map(f => URL.createObjectURL(f)));
    setError('');
    setStatus('');
    setDownloadUrl(null);
    setProgress({ done: 0, total: 0 });
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleImageSelect(e.dataTransfer.files);
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const handleGenerate = async () => {
    if (images.length === 0) return;
    setGenerating(true);
    setError('');
    setStatus('Starting...');
    setDownloadUrl(null);
    setProgress({ done: 0, total: images.length * count });

    const formData = new FormData();
    images.forEach(img => formData.append('images', img));
    formData.append('intensity', intensity);
    formData.append('fileType', fileType);
    formData.append('count', count);

    try {
      const res = await axios.post(`${API}/api/spoofer/generate`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const { jobId, total } = res.data;
      jobIdRef.current = jobId;
      localStorage.setItem('spoofJobId', jobId);
      if (!mountedRef.current) return; // left the page meanwhile — resumed on return
      setProgress({ done: 0, total });
      setStatus(`Generating 0 of ${total}...`);

      pollRef.current = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/api/spoofer/status/${jobId}?t=${Date.now()}`);
          const job = statusRes.data;

          if (job.status === 'running') {
            setProgress({ done: job.done, total: job.total });
            setStatus(`Generating ${job.done} of ${job.total}...`);
            showActivity(`Spoofing ${job.done}/${job.total}...`);
          } else if (job.status === 'zipping') {
            setProgress({ done: job.total, total: job.total });
            setStatus('Creating zip archive...');
          } else if (job.status === 'done') {
            stopPolling();
            localStorage.removeItem('spoofJobId');
            hideActivity();
            setProgress({ done: job.total, total: job.total });
            setGenerating(false);
            setStatus('✓ Ready to download');
            setDownloadUrl(job.url);
          } else if (job.status === 'cancelled') {
            stopPolling();
            localStorage.removeItem('spoofJobId');
            setStatus('Cancelled.');
            hideActivity();
            setGenerating(false);
          } else if (job.status === 'error') {
            stopPolling();
            localStorage.removeItem('spoofJobId');
            setError(job.error || 'Unknown error');
            hideActivity();
            setGenerating(false);
          }
        } catch (err) {
          if (err.response?.status === 404) { stopPolling(); localStorage.removeItem('spoofJobId'); return; }
          stopPolling();
          hideActivity();
          setError('Lost connection to server.');
          setGenerating(false);
        }
      }, 500);

    } catch (err) {
      const e = err.response?.data?.error;
      setError(typeof e === 'string' ? e : err.message);
      setGenerating(false);
    }
  };

  const handleCancel = async () => {
    stopPolling();
    hideActivity();
    localStorage.removeItem('spoofJobId');
    if (jobIdRef.current) {
      try { await axios.delete(`${API}/api/spoofer/cancel/${jobIdRef.current}`); } catch (e) {}
    }
    setGenerating(false);
    setStatus('Cancelled.');
  };

  const handleInitialSpoof = async () => {
    if (images.length === 0) return;
    setGenerating(true);
    setError('');
    setStatus('Applying initial spoof...');
    showActivity('Applying initial spoof...');
    setDownloadUrl(null);

    const formData = new FormData();
    images.forEach(img => formData.append('images', img));

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/spoofer/initial-spoof`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Spoof failed');
      }

      const contentType = res.headers.get('content-type');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = contentType.includes('zip')
        ? `initial_spoof_${images.length}_images.zip`
        : `${images[0].name.replace(/\.[^.]+$/, '')}_spoofed.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      setStatus(`✓ Initial spoof complete — ${images.length} file${images.length > 1 ? 's' : ''}`);
    } catch (err) {
      setError(err.message);
    }
    hideActivity();
    setGenerating(false);
  };

  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Spoofer</h1>
          <p>Generate spoofed copies of an image to bypass Reddit's duplicate detection.</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: previews.length > 0 ? '1fr 1fr' : '1fr', gap: '24px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Upload */}
          <div className="card">
            <h2>{fileType === 'gif' ? 'GIFs' : 'Images'}</h2>
            <div
              onDrop={handleDrop}
              onDragOver={e => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: '2px dashed #333', borderRadius: '10px', padding: '24px',
                textAlign: 'center', cursor: 'pointer', color: '#555',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#ff4500'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#333'; }}
            >
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>📁</div>
              <div style={{ fontSize: '14px' }}>Drop images here or click to upload</div>
              <div style={{ fontSize: '12px', marginTop: '4px' }}>{fileType === 'gif' ? 'GIF files — multiple supported' : 'JPG, PNG — multiple files supported'}</div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={fileType === 'gif' ? 'image/gif' : 'image/jpeg,image/png'}
              multiple
              style={{ display: 'none' }}
              onChange={e => { if (e.target.files.length > 0) handleImageSelect(e.target.files); }}
            />
            {images.length > 0 && (
              <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {images.map((img, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#0f0f0f', borderRadius: '8px', padding: '8px 12px' }}>
                    <div>
                      <div style={{ fontSize: '13px', color: '#ffffff' }}>{img.name}</div>
                      <div style={{ fontSize: '11px', color: '#555' }}>{(img.size / 1024 / 1024).toFixed(1)} MB</div>
                    </div>
                    <button onClick={() => removeImage(idx)} style={{ background: 'transparent', border: 'none', color: '#ff4500', cursor: 'pointer', fontSize: '16px' }}>✕</button>
                  </div>
                ))}
                <div style={{ fontSize: '11px', color: '#555', textAlign: 'right' }}>{images.length} file{images.length > 1 ? 's' : ''} selected</div>
              </div>
            )}
          </div>

          {/* Settings */}
          <div className="card">
            <h2>Settings</h2>

            {/* File type selector */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '12px', color: '#aaaaaa', display: 'block', marginBottom: '8px' }}>FILE TYPE</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[{ key: 'image', label: '🖼 Images' }, { key: 'gif', label: '🎞 GIFs' }].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => { setFileType(opt.key); setImages([]); setPreviews([]); }}
                    style={{
                      flex: 1, padding: '10px', borderRadius: '8px', border: 'none',
                      cursor: 'pointer', fontWeight: '600', fontSize: '13px',
                      background: fileType === opt.key ? '#3b82f6' : '#2a2a2a',
                      color: fileType === opt.key ? '#ffffff' : '#aaaaaa',
                    }}
                  >{opt.label}</button>
                ))}
              </div>
            </div>
            {/* Intensity selector */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '12px', color: '#aaaaaa', display: 'block', marginBottom: '8px' }}>INTENSITY</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {['medium', 'high'].map(opt => (
                  <button
                    key={opt}
                    onClick={() => setIntensity(opt)}
                    style={{
                      flex: 1, padding: '10px', borderRadius: '8px', border: 'none',
                      cursor: 'pointer', fontWeight: '600', fontSize: '13px',
                      background: intensity === opt ? '#ff4500' : '#2a2a2a',
                      color: intensity === opt ? '#ffffff' : '#aaaaaa',
                    }}
                  >
                    {opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </button>
                ))}
              </div>
              <div style={{ fontSize: '11px', color: '#555', marginTop: '6px' }}>
                {intensity === 'medium'
                  ? 'Subtle changes, visually close to original. Recommended for most use cases.'
                  : 'More aggressive changes, higher variation. Better for repeated heavy use.'}
              </div>
            </div>

            {/* Count */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '12px', color: '#aaaaaa', display: 'block', marginBottom: '8px' }}>
                NUMBER OF COPIES — <span style={{ color: '#ff4500' }}>{count}</span>
              </label>
              <input
                type="range"
                min="1"
                max="1000"
                value={count}
                onChange={e => setCount(parseInt(e.target.value))}
                style={{ width: '100%', accentColor: '#ff4500' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#555', marginTop: '4px' }}>
                <span>1</span><span>250</span><span>500</span><span>750</span><span>1000</span>
              </div>
              <input
                type="number"
                min="1"
                max="1000"
                value={count}
                onChange={e => setCount(Math.min(1000, Math.max(1, parseInt(e.target.value) || 1)))}
                style={{ marginTop: '8px', width: '100px', fontSize: '13px' }}
              />
            </div>

            <div style={{ padding: '8px 12px', background: '#0f0f0f', borderRadius: '8px', fontSize: '11px', color: '#555' }}>
              Files named using original filenames: original_name_001.jpg, original_name_002.jpg...
            </div>
          </div>

          {/* Generate / Progress */}
          <div className="card">
            {generating ? (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <span style={{ fontSize: '13px', color: '#aaaaaa' }}>{status}</span>
                  <button className="btn-secondary" style={{ fontSize: '12px', padding: '4px 12px' }} onClick={handleCancel}>
                    ✕ Cancel
                  </button>
                </div>
                <div style={{ background: '#2a2a2a', borderRadius: '8px', height: '12px', overflow: 'hidden' }}>
                  <div style={{
                    background: '#ff4500', height: '100%', width: `${pct}%`,
                    transition: 'width 0.3s ease', borderRadius: '8px',
                  }} />
                </div>
                <div style={{ fontSize: '12px', color: '#555', marginTop: '6px', textAlign: 'right' }}>
                  {progress.done} / {progress.total} ({pct}%)
                </div>
              </div>
            ) : (
              <div>
                {downloadUrl && (
                  <a
                    href={downloadUrl}
                    download
                    style={{
                      display: 'block', textAlign: 'center', padding: '10px',
                      background: '#22c55e', color: '#ffffff', borderRadius: '8px',
                      textDecoration: 'none', fontWeight: '600', fontSize: '13px',
                      marginBottom: '12px'
                    }}
                  >
                    ⬇️ Download Ready — Click to Save
                  </a>
                )}
                {status && !downloadUrl && (
                  <div style={{ fontSize: '13px', color: status.startsWith('✓') ? '#22c55e' : '#aaaaaa', marginBottom: '12px' }}>
                    {status}
                  </div>
                )}
                {error && (
                  <div style={{ fontSize: '13px', color: '#ef4444', marginBottom: '12px' }}>
                    ✗ Error: {error}
                  </div>
                )}
                <button
                  onClick={handleGenerate}
                  disabled={images.length === 0}
                  style={{ width: '100%', opacity: images.length > 0 ? 1 : 0.5 }}
                >
                 🎲 Generate {images.length > 0 ? `${images.length} × ${count} = ${images.length * count}` : count} {fileType === 'gif' ? 'GIF Variants' : 'Copies'}
                </button>
                {fileType === 'image' && (
                  <>
                    <button
                      onClick={handleInitialSpoof}
                      disabled={images.length === 0 || generating}
                      style={{
                        width: '100%', marginTop: '8px',
                        background: '#2a2a2a', color: '#aaaaaa',
                        opacity: images.length > 0 && !generating ? 1 : 0.5,
                        cursor: generating ? 'wait' : 'pointer'
                      }}
                    >
                      {generating && status === 'Applying initial spoof...'
                        ? '⏳ Processing...'
                        : `🔒 Initial Spoof ${images.length > 1 ? `(${images.length} files)` : ''}`}
                    </button>
                    <div style={{ fontSize: '11px', color: '#555', marginTop: '4px' }}>
                      One-time base transform for vault upload — crop, flip, recompress.
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Preview */}
        {previews.length > 0 && (
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <h2>Preview</h2>
            {previews.map((src, idx) => (
              <img key={idx} src={src} alt={`preview ${idx + 1}`}
                style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain', borderRadius: '8px' }} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Spoofer;